import React, { useRef, useEffect, useState } from "react";
import "./contextMenu.scss";
import TagService from "../../../services/TagService/tag.service";
import { Tag } from "../../../models/Tag/tag.model";
import { CaseDetail } from "../../../models/CaseDetail/caseDetail.model";
import AuthContainer from "../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../store/reducers/authReducer";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import { Modal, Input } from "antd";
import { TagEnum } from "../../../enums/tag.enum";

interface ContextMenuProps extends AuthReducerProps {
  visible: boolean;
  position: { x: number; y: number };
  selectedRow: CaseDetail;
  onClose: () => void;
  onUpdate: (caseId: number, tags: Tag[]) => void;
}

const isInterestingTag = (tag: Tag) =>
  tag?.name?.toLowerCase() === TagEnum.INTERESTING;

const ContextMenu: React.FC<ContextMenuProps> = ({
  visible,
  position,
  selectedRow,
  onClose,
  onUpdate,
  userRole,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  const [tags, setTags] = useState<Tag[]>([]);
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [reasonModalTagId, setReasonModalTagId] = useState<number | null>(null);
  const [reasonValue, setReasonValue] = useState("");
  const [reasonSubmitting, setReasonSubmitting] = useState(false);

  const applyTagUpdate = (tagId: number, reason?: string) => {
    if (!selectedRow.tags || !selectedRow.id) return;
    const tagIds = selectedRow.tags.map((t: Tag) => t.id);
    const hasTag = tagIds.includes(tagId);
    const newTagIds = hasTag
      ? tagIds.filter((id) => id !== tagId)
      : [...tagIds.filter(Boolean), tagId].filter(
          (id): id is number => id !== undefined
        );
    TagService.updateAdminTags(
      selectedRow.id,
      userRole as UserRoleEnum,
      newTagIds,
      (updatedTags: Tag[]) => {
        selectedRow.id && onUpdate(selectedRow.id, updatedTags);
      },
      () => {},
      () => {},
      reason ? { reason } : undefined
    );
  };

  const handleTagClick = (tag: Tag) => {
    if (!tag?.id) return;
    const addingInteresting =
      isInterestingTag(tag) && !selectedRow?.parsedTagIds?.includes(tag.id);
    if (addingInteresting) {
      setReasonModalTagId(tag.id);
      setReasonValue("");
      setReasonModalVisible(true);
    } else {
      applyTagUpdate(tag.id);
      onClose();
    }
  };

  const handleReasonSubmit = () => {
    if (reasonModalTagId == null || !selectedRow?.id) return;
    setReasonSubmitting(true);
    if (!selectedRow.tags) {
      setReasonSubmitting(false);
      return;
    }
    const tagIds = selectedRow.tags.map((t: Tag) => t.id);
    const newTagIds = [...tagIds.filter(Boolean), reasonModalTagId].filter(
      (id): id is number => id !== undefined
    );
    TagService.updateAdminTags(
      selectedRow.id,
      userRole as UserRoleEnum,
      newTagIds,
      (updatedTags: Tag[]) => {
        selectedRow.id && onUpdate(selectedRow.id, updatedTags);
        setReasonModalVisible(false);
        setReasonModalTagId(null);
        setReasonValue("");
        onClose();
      },
      () => {},
      () => setReasonSubmitting(false),
      reasonValue.trim() ? { reason: reasonValue.trim() } : undefined
    );
  };

  const handleReasonCancel = () => {
    setReasonModalVisible(false);
    setReasonModalTagId(null);
    setReasonValue("");
  };

  useEffect(() => {
    TagService.fetchAdminTags(
      userRole as UserRoleEnum,
      (fetchedTags: Tag[]) => setTags(fetchedTags),
      () => {},
      () => {}
    );
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reasonModalVisible) return;
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [onClose, reasonModalVisible]);

  if (!visible) return null;

  return (
    <>
      <div
        ref={menuRef}
        className="context-menu"
        style={{ top: position.y, left: position.x }}
      >
        {tags.map((tag) => (
          <button
            key={tag?.id}
            type="button"
            className="menu-item"
            onClick={() => handleTagClick(tag)}
          >
            <span
              className="menu-item__legend"
              style={{ backgroundColor: tag?.color }}
            />
            <span className="menu-item__label">
              {tag?.name?.split("_").join(" ")}
            </span>
            {selectedRow?.parsedTagIds?.includes(tag?.id) && (
              <span className="menu-item__checked">✓</span>
            )}
          </button>
        ))}
      </div>
      <Modal
        title="Why is this case interesting?"
        visible={reasonModalVisible}
        onOk={handleReasonSubmit}
        onCancel={handleReasonCancel}
        okText="Save"
        confirmLoading={reasonSubmitting}
        destroyOnClose
        okButtonProps={{}}
      >
        <Input.TextArea
          placeholder="Enter reason (optional)"
          value={reasonValue}
          onChange={(e) => setReasonValue(e.target.value)}
          rows={4}
          autoFocus
        />
      </Modal>
    </>
  );
};

export default AuthContainer(ContextMenu);
